import React, { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    Stack,
    Button,
    TextField,
    Alert,
    Skeleton,
    Divider,
    useTheme,
} from '@mui/material';
import { PlayArrow, Send } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { usePreviewNotificationTemplate } from '../../hooks/notificationTemplates/usePreviewNotificationTemplate';
import parseError from '../../utils/parseError';

const buildSampleScaffold = (variables = []) => {
    if (!Array.isArray(variables) || variables.length === 0) {
        return JSON.stringify({}, null, 2);
    }
    const obj = {};
    variables.forEach((name, idx) => {
        obj[name] = `Sample ${idx + 1}`;
    });
    return JSON.stringify(obj, null, 2);
};

/**
 * Right-hand pane that renders the template against an editable
 * `sample_vars` JSON object via POST /preview, and delegates Test Send
 * to the parent (so the parent can wire its own dialog).
 */
const PreviewPane = ({ templateId, variables, channel, onTestSend }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();

    const [sampleText, setSampleText] = useState(() => buildSampleScaffold(variables));
    const [jsonError, setJsonError] = useState(null);

    // Re-seed the JSON editor whenever the variable list changes — but only if
    // the operator hasn't already started filling it in.
    const initialSample = useMemo(() => buildSampleScaffold(variables), [variables]);
    useEffect(() => {
        setSampleText((current) => {
            // If the current text is still the previous scaffold, refresh it.
            try {
                const parsed = JSON.parse(current);
                const isEmpty = Object.values(parsed).every(
                    (v) => typeof v === 'string' && /^Sample \d+$/.test(v)
                );
                return isEmpty ? initialSample : current;
            } catch {
                return current;
            }
        });
    }, [initialSample]);

    const previewMutation = usePreviewNotificationTemplate();

    const handlePreview = () => {
        let parsed;
        try {
            parsed = sampleText.trim() ? JSON.parse(sampleText) : {};
            setJsonError(null);
        } catch (e) {
            setJsonError(isArabic ? 'صيغة JSON غير صحيحة' : 'Invalid JSON');
            return;
        }
        if (!templateId) return;
        previewMutation.mutate({ id: templateId, data: { sample_vars: parsed } });
    };

    const rendered = previewMutation.data?.data;
    const renderedSubject = rendered?.subject;
    const renderedBody = rendered?.body;
    const apiError = previewMutation.error
        ? parseError(previewMutation.error)
        : null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                position: { md: 'sticky' },
                top: { md: 16 },
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight={700}>
                    {isArabic ? 'معاينة وإرسال تجريبي' : 'Preview & test send'}
                </Typography>
                {channel === 'email' && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<Send />}
                        onClick={onTestSend}
                        disabled={!templateId}
                    >
                        {isArabic ? 'إرسال تجريبي' : 'Test send'}
                    </Button>
                )}
            </Stack>

            {!templateId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {isArabic
                        ? 'احفظ القالب أولاً حتى تتمكن من المعاينة.'
                        : 'Save the template first to preview it.'}
                </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {isArabic
                    ? 'قيم تجريبية للمتغيرات (JSON)'
                    : 'Sample variables (JSON)'}
            </Typography>
            <TextField
                fullWidth
                multiline
                minRows={6}
                value={sampleText}
                onChange={(e) => setSampleText(e.target.value)}
                error={!!jsonError}
                helperText={jsonError || ''}
                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
                disabled={!templateId}
            />

            <Box sx={{ mt: 1.5 }}>
                <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={handlePreview}
                    disabled={!templateId || previewMutation.isPending}
                    fullWidth
                >
                    {previewMutation.isPending
                        ? (isArabic ? 'جاري المعاينة...' : 'Rendering...')
                        : (isArabic ? 'معاينة' : 'Render preview')}
                </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {previewMutation.isPending ? (
                <Stack spacing={1}>
                    <Skeleton width="40%" />
                    <Skeleton variant="rectangular" height={140} />
                </Stack>
            ) : apiError ? (
                <Alert severity="error">{apiError}</Alert>
            ) : rendered ? (
                <Stack spacing={1.5}>
                    {renderedSubject && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {isArabic ? 'العنوان' : 'Subject'}
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={700}>
                                {renderedSubject}
                            </Typography>
                        </Box>
                    )}
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {isArabic ? 'النص' : 'Body'}
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: 'background.default',
                                border: `1px solid ${theme.palette.divider}`,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: channel === 'email' ? 'inherit' : 'monospace',
                                fontSize: 14,
                                maxHeight: 320,
                                overflowY: 'auto',
                            }}
                        >
                            {renderedBody || (isArabic ? '(فارغ)' : '(empty)')}
                        </Box>
                    </Box>
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {isArabic
                        ? 'اضغط معاينة لرؤية الناتج.'
                        : 'Click Render preview to see the output.'}
                </Typography>
            )}
        </Paper>
    );
};

export default PreviewPane;
